import { TFunction } from 'i18next';

const localeGroups = {
  en: [
    'en',
    'en-US',
    'en-GB',
    'en-CA',
    'en-AU',
    'en-NZ',
    'en-IE',
    'en-ZA',
    'en-IN',
  ],
  'es-419': [
    'es',
    'es-419',
    'es-ES',
    'es-MX',
    'es-AR',
    'es-CO',
    'es-CL',
    'es-PE',
    'es-VE',
    'es-UY',
    'es-PY',
    'es-BO',
    'es-EC',
    'es-GT',
    'es-HN',
    'es-SV',
    'es-NI',
    'es-CR',
    'es-PA',
    'es-CU',
    'es-DO',
    'es-PR',
  ],
  'pt-BR': [
    'pt',
    'pt-BR',
    'pt-PT',
    'pt-AO',
    'pt-MZ',
    'pt-GW',
    'pt-ST',
    'pt-CV',
    'pt-TL',
  ],
  fr: ['fr', 'fr-FR', 'fr-CA', 'fr-BE', 'fr-CH', 'fr-LU', 'fr-MC'],
  de: ['de', 'de-DE', 'de-AT', 'de-CH', 'de-BE', 'de-LI', 'de-LU'],
  tr: ['tr', 'tr-TR', 'tr-CY'],
  it: ['it', 'it-IT', 'it-CH', 'it-SM', 'it-VA'],
  ja: ['ja', 'ja-JP'],
  vi: ['vi', 'vi-VN'],
  ko: ['ko', 'ko-KR'],
  pl: ['pl', 'pl-PL'],
  ro: ['ro', 'ro-RO'],
  da: ['da', 'da-DK'],
  hi: ['hi', 'hi-IN'],
  ms: ['ms', 'ms-MY', 'ms-SG', 'ms-BN'],
  bn: ['bn', 'bn-BD', 'bn-IN'],
  id: ['id', 'id-ID'],
  mr: ['mr', 'mr-IN'],
  te: ['te', 'te-IN'],
  ta: ['ta', 'ta-IN', 'ta-LK', 'ta-SG'],
  th: ['th', 'th-TH'],
  gu: ['gu', 'gu-IN'],
  kn: ['kn', 'kn-IN'],
  uk: ['uk', 'uk-UA'],
  ml: ['ml', 'ml-IN'],
  nl: ['nl', 'nl-NL', 'nl-BE'],
  fil: ['fil', 'tl', 'tl-PH'],
  sr: ['sr', 'sr-RS', 'sr-Latn', 'sr-Cyrl'],
  cs: ['cs', 'cs-CZ'],
  el: ['el', 'el-GR', 'el-CY'],
  sv: ['sv', 'sv-SE', 'sv-FI'],
  hu: ['hu', 'hu-HU'],
  ca: ['ca', 'ca-ES'],
  fi: ['fi', 'fi-FI'],
  bg: ['bg', 'bg-BG'],
  hr: ['hr', 'hr-HR'],
  nb: ['nb', 'no', 'nb-NO'],
  sk: ['sk', 'sk-SK'],
  lt: ['lt', 'lt-LT'],
  sl: ['sl', 'sl-SI'],
  lv: ['lv', 'lv-LV'],
  et: ['et', 'et-EE'],
} as const;

const canonicalLocales: Record<string, string[]> = Object.entries(
  localeGroups,
).reduce(
  (acc, [canonical, variants]) => {
    variants.forEach((v) => {
      acc[v] = [canonical];
    });
    return acc;
  },
  {} as Record<string, string[]>,
);

const getCanonicalLocale = (lng?: string): string => {
  if (!lng) {
    return 'en';
  }

  return (
    canonicalLocales[lng][0] || canonicalLocales[lng.split('-')[0]][0] || 'en'
  );
};

const getLanguages = (): {
  getText: (t: TFunction) => string;
  value: string;
}[] => {
  return [
    {
      getText: (t: TFunction): string => t(`languages:english`, 'English'),
      value: 'en',
    },
    {
      getText: (t: TFunction): string => t(`languages:spanish`, 'Spanish'),
      value: 'es-419',
    },
    {
      getText: (t: TFunction): string =>
        t(`languages:portuguese`, 'Portuguese'),
      value: 'pt-BR',
    },
    {
      getText: (t: TFunction): string => t(`languages:french`, 'French'),
      value: 'fr',
    },
    {
      getText: (t: TFunction): string => t(`languages:german`, 'German'),
      value: 'de',
    },
    {
      getText: (t: TFunction): string => t(`languages:turkish`, 'Turkish'),
      value: 'tr',
    },
    {
      getText: (t: TFunction): string => t(`languages:italian`, 'Italian'),
      value: 'it',
    },
    {
      getText: (t: TFunction): string => t('languages:japanese', 'Japanese'),
      value: 'ja',
    },
    {
      getText: (t: TFunction): string =>
        t('languages:vietnamese', 'Vietnamese'),
      value: 'vi',
    },
    {
      getText: (t: TFunction): string => t('languages:korean', 'Korean'),
      value: 'ko',
    },
    {
      getText: (t: TFunction): string => t('languages:polish', 'Polish'),
      value: 'pl',
    },
    {
      getText: (t: TFunction): string => t('languages:romanian', 'Romanian'),
      value: 'ro',
    },
    {
      getText: (t: TFunction): string => t('languages:danish', 'Danish'),
      value: 'da',
    },
    {
      getText: (t: TFunction): string => t('languages:hindi', 'Hindi'),
      value: 'hi',
    },
    {
      getText: (t: TFunction): string => t('languages:malay', 'Malay'),
      value: 'ms',
    },
    {
      getText: (t: TFunction): string => t('languages:bengali', 'Bengali'),
      value: 'bn',
    },
    {
      getText: (t: TFunction): string =>
        t('languages:indonesian', 'Indonesian'),
      value: 'id',
    },
    {
      getText: (t: TFunction): string => t('languages:marathi', 'Marathi'),
      value: 'mr',
    },
    {
      getText: (t: TFunction): string => t('languages:telugu', 'Telugu'),
      value: 'te',
    },
    {
      getText: (t: TFunction): string => t('languages:tamil', 'Tamil'),
      value: 'ta',
    },
    {
      getText: (t: TFunction): string => t('languages:thai', 'Thai'),
      value: 'th',
    },
    {
      getText: (t: TFunction): string => t('languages:gujarati', 'Gujarati'),
      value: 'gu',
    },
    {
      getText: (t: TFunction): string => t('languages:kannada', 'Kannada'),
      value: 'kn',
    },
    {
      getText: (t: TFunction): string => t('languages:ukrainian', 'Ukrainian'),
      value: 'uk',
    },
    {
      getText: (t: TFunction): string => t('languages:malayalam', 'Malayalam'),
      value: 'ml',
    },
    {
      getText: (t: TFunction): string => t('languages:dutch', 'Dutch'),
      value: 'nl',
    },
    {
      getText: (t: TFunction): string => t('languages:filipino', 'Filipino'),
      value: 'fil',
    },
    {
      getText: (t: TFunction): string => t('languages:serbian', 'Serbian'),
      value: 'sr',
    },
    {
      getText: (t: TFunction): string => t('languages:czech', 'Czech'),
      value: 'cs',
    },
    {
      getText: (t: TFunction): string => t('languages:greek', 'Greek'),
      value: 'el',
    },
    {
      getText: (t: TFunction): string => t('languages:swedish', 'Swedish'),
      value: 'sv',
    },
    {
      getText: (t: TFunction): string => t('languages:hungarian', 'Hungarian'),
      value: 'hu',
    },
    {
      getText: (t: TFunction): string => t('languages:catalan', 'Catalan'),
      value: 'ca',
    },
    {
      getText: (t: TFunction): string => t('languages:finnish', 'Finnish'),
      value: 'fi',
    },
    {
      getText: (t: TFunction): string => t('languages:bulgarian', 'Bulgarian'),
      value: 'bg',
    },
    {
      getText: (t: TFunction): string => t('languages:croatian', 'Croatian'),
      value: 'hr',
    },
    {
      getText: (t: TFunction): string => t('languages:norwegian', 'Norwegian'),
      value: 'nb',
    },
    {
      getText: (t: TFunction): string => t('languages:slovak', 'Slovak'),
      value: 'sk',
    },
    {
      getText: (t: TFunction): string =>
        t('languages:lithuanian', 'Lithuanian'),
      value: 'lt',
    },
    {
      getText: (t: TFunction): string => t('languages:slovenian', 'Slovenian'),
      value: 'sl',
    },
    {
      getText: (t: TFunction): string => t('languages:latvian', 'Latvian'),
      value: 'lv',
    },
    {
      getText: (t: TFunction): string => t('languages:estonian', 'Estonian'),
      value: 'et',
    },
  ];
};

export { canonicalLocales, getCanonicalLocale, getLanguages };
